import {useAgencyQuery} from "@/api/agencyApi";
import {useCompanyQuery} from "@/api/companyApi";
import {useEndImpersonationMutation} from "@/api/miscApi";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import Button from "@/components/Common/Button";
import {brandImpersonationAtom} from "@/store/jotaiAtoms";
import type {TBrandImpersonation} from "@/store/types";
import {switchSession} from "@/utils/impersonationUtils";
import {Link} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const BrandImpersonationBar: FC<{impersonation: TBrandImpersonation}> = ({impersonation}) => {
  const {t} = useTranslation("impersonate");
  const setBrandImpersonation = useSetAtom(brandImpersonationAtom);
  const {mutate: endImpersonation, isPending: isEndingImpersonation} = useEndImpersonationMutation();
  const companyId = "companyId" in impersonation ? impersonation.companyId : undefined;
  const agencyId = "agencyId" in impersonation ? impersonation.agencyId : undefined;
  const {data: company} = useCompanyQuery({companyId: companyId!});
  const {data: agency} = useAgencyQuery({agencyId: agencyId!});

  const account = companyId
    ? {name: company?.company_name, picture: company?.picture}
    : {name: agency?.name, picture: agency?.picture};

  return (
    <div className="bg-pretty-pink-200 fixed inset-x-0 top-0 z-100000 flex h-14 items-center gap-4 px-4">
      <Button
        buttonStyle="White"
        onClick={() =>
          endImpersonation(undefined, {
            onSettled: () => {
              setBrandImpersonation(null);
              switchSession({access: impersonation.adminAccess, refresh: impersonation.adminRefresh});
            },
          })
        }
        isLoading={isEndingImpersonation}
        className="h-8 shrink-0 gap-2 px-4 text-sm">
        <RightArrowIcon className="size-2.5 rotate-180" />
        {t("back_to_admin_mode")}
      </Button>
      <Link
        {...(companyId
          ? {to: "/company/$companyId", params: {companyId}}
          : {to: "/agency/$agencyId", params: {agencyId: agencyId!}})}
        className="text-neutral-black hover:text-primary-dark-solid flex min-w-0 items-center gap-2 no-underline">
        <img
          src={account.picture ?? CompanyLogoPlaceholder}
          alt=""
          className="size-7 shrink-0 rounded-full object-cover"
        />
        <span className="truncate text-base font-semibold">{account.name}</span>
      </Link>
      <span className="text-danger-dark-red truncate text-sm font-medium max-md:hidden">
        {companyId ? t("impersonating_bar") : t("impersonating_bar_agency")}
      </span>
    </div>
  );
};

export default BrandImpersonationBar;
